import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  I,
  PageHeader,
  PageHeaderSkeleton,
  Pill,
  Row,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { InviteByEmailModal } from '../../invites';
import type { GroupMemberView } from '../../../lib/views/groups';
import { useInfiniteGroupMembers } from '../../../lib/queries/groups';
import { useFormatDate } from '../../../lib/format-date.client';
import { useSpace } from '../../../lib/queries/spaces';
import {
  useLeaveGroup,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from '../../../lib/mutations/groups';
import { useInviteToSpace } from '../../../lib/mutations/spaces';
import { useGroupLeaveFlow } from '../../group/use-group-leave-flow';
import { formatGroupMemberError } from '../../groups/format-group-member-error';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import { failureFromError } from '../../../lib/api-error';
import {
  MembersTableSkeleton,
  MembersTablePanel,
  buildMemberColumns,
  memberName,
} from '../../group/admin/members-table';

type Props = {
  readonly id: string;
  readonly viewerUserId: string;
};

export function SpaceMembersSection({ id, viewerUserId }: Props) {
  const t = useTranslations('Group');
  const detailQuery = useSpace(id);
  const membersQuery = useInfiniteGroupMembers(id, { pageSize: 100 });
  const fmt = useFormatDate();

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = membersQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const navigate = useNavigate();
  const setRoleMutation = useSetGroupMemberRole();
  const removeMutation = useRemoveGroupMember();
  const leaveMutation = useLeaveGroup();
  const inviteMutation = useInviteToSpace();

  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const remove = useConfirmDialog<GroupMemberView>();
  const demote = useConfirmDialog<GroupMemberView>();

  const leaveFlow = useGroupLeaveFlow({
    leave: (handlers) =>
      leaveMutation.mutate(
        { id, kind: 'space' },
        {
          onSuccess: () => {
            navigate({ to: '/groups' });
            handlers.onSuccess();
          },
          onError: handlers.onError,
        },
      ),
    fallbackMessage: t('membersAdmin.errorLeave'),
  });

  const isPending = setRoleMutation.isPending || removeMutation.isPending;

  const handlePromote = (m: GroupMemberView) => {
    setError(null);
    setRoleMutation.mutate(
      { groupId: id, kind: 'space', userId: m.userId, role: 'leader' },
      {
        onError: (err) =>
          setError(formatGroupMemberError(err, t('membersAdmin.errorSetRole'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmDemote = () => {
    if (demote.target === null) return;
    const target = demote.target;
    setError(null);
    setRoleMutation.mutate(
      { groupId: id, kind: 'space', userId: target.userId, role: 'member' },
      {
        onSettled: () => demote.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, t('membersAdmin.errorSetRole'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmRemove = () => {
    if (remove.target === null) return;
    const target = remove.target;
    setError(null);
    removeMutation.mutate(
      { groupId: id, userId: target.userId },
      {
        onSettled: () => remove.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, t('membersAdmin.errorRemove'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const showSkeleton = useDelayedLoading(membersQuery.data === undefined);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton withAction />
        <MembersTableSkeleton rows={6} />
      </Stack>
    );
  }
  if (membersQuery.data === undefined) return null;

  const allMembers = membersQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const space = detailQuery.data ?? null;
  const spaceName = space?.name ?? t('membersAdmin.groupNameFallback');
  const isClosed = space?.access === 'private';

  const viewerMembership = allMembers.find((m) => m.userId === viewerUserId);
  const viewerIsOnRoster = viewerMembership !== undefined;
  const leaderCount = allMembers.filter((m) => m.role === 'leader').length;
  const viewerIsOnlyLeader =
    viewerMembership?.role === 'leader' && leaderCount <= 1;

  const columns = buildMemberColumns<GroupMemberView>({
    viewerUserId,
    t,
    fmt,
    renderRole: (m) => (
      <Pill tone={m.role === 'leader' ? 'burgundy' : 'neutral'}>
        {m.role === 'leader' ? t('membersAdmin.roleLeader') : t('membersAdmin.roleMember')}
      </Pill>
    ),
    renderActions: (m, { name }) => {
      const isLeader = m.role === 'leader';
      return (
        <Row gap="xs" align="center" justify="flex-end">
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={t('membersAdmin.manageMemberAria', { name })}
              />
            }
          >
            {!isLeader ? (
              <DropdownMenuItem
                onSelect={() => handlePromote(m)}
                disabled={isPending}
              >
                {t('membersAdmin.promote')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => demote.request(m)}
                disabled={isPending}
              >
                {t('membersAdmin.demote')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              disabled={isPending}
              onSelect={() => remove.request(m)}
            >
              {t('membersAdmin.removeFromGroup')}
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      );
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('membersAdmin.heading')}
        subtitle={t('membersAdmin.subtitle')}
        actions={
          <Row gap="sm" align="center" wrap>
            {!viewerIsOnRoster ? null : viewerIsOnlyLeader ? (
              <Tooltip content={t('membersAdmin.onlyLeaderTooltip')}>
                <span tabIndex={0}>
                  <Button variant="secondary" size="md" disabled>
                    {t('membersAdmin.leaveGroup')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={leaveFlow.openConfirm}
              >
                {t('membersAdmin.leaveGroup')}
              </Button>
            )}
            {isClosed ? (
              <Button
                variant="primary"
                size="md"
                icon={I.mail}
                onClick={() => setInviteOpen(true)}
              >
                {t('membersAdmin.inviteByEmail')}
              </Button>
            ) : null}
          </Row>
        }
      />

      <MembersTablePanel<GroupMemberView>
        members={allMembers}
        columns={columns}
        t={t}
        searchPlaceholder={t('membersAdmin.searchPlaceholder')}
        emptyMessage={t('membersAdmin.emptyTable')}
        totalCount={detailQuery.data?.memberCount ?? allMembers.length}
        countSummary={(shown, total) => (
          <Body size="sm" color="mute">
            {t('membersAdmin.countSummary', { count: shown, total })}
          </Body>
        )}
        groupKind="space"
      />

      {error !== null ? (
        <span role="alert" aria-live="polite">
          <Body size="sm" color="destructive">{error}</Body>
        </span>
      ) : null}

      {isClosed && space !== null ? (
        <InviteByEmailModal
          kind="email"
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          title={t('membersAdmin.inviteModalTitle', { name: spaceName })}
          description={t('membersAdmin.inviteModalDescription')}
          onInvite={async (email) => {
            try {
              await inviteMutation.mutateAsync({ id: space.id, emails: [email] });
              return { ok: true };
            } catch (err) {
              return { ok: false, error: failureFromError(err, t('membersAdmin.inviteError')).message };
            }
          }}
        />
      ) : null}

      <AlertDialog
        open={remove.open}
        onOpenChange={remove.onOpenChange}
        title={
          remove.target !== null
            ? t('membersAdmin.removeDialogTitle', { name: memberName(remove.target) })
            : t('membersAdmin.removeDialogTitleFallback')
        }
        description={
          remove.target !== null
            ? t('membersAdmin.removeDialogBody', { name: memberName(remove.target) })
            : ''
        }
        actionLabel={t('membersAdmin.removeDialogConfirm')}
        cancelLabel={t('membersAdmin.removeDialogCancel')}
        destructive
        onAction={confirmRemove}
      />

      <AlertDialog
        open={demote.open}
        onOpenChange={demote.onOpenChange}
        title={
          demote.target !== null
            ? t('membersAdmin.demoteDialogTitle', { name: memberName(demote.target) })
            : t('membersAdmin.demoteDialogTitleFallback')
        }
        description={t('membersAdmin.demoteDialogBody')}
        actionLabel={t('membersAdmin.demoteDialogConfirm')}
        cancelLabel={t('membersAdmin.demoteDialogCancel')}
        destructive
        onAction={confirmDemote}
      />

      <AlertDialog
        open={leaveFlow.confirmOpen}
        onOpenChange={leaveFlow.setConfirmOpen}
        title={t('membersAdmin.leaveDialogTitle')}
        description={t('membersAdmin.leaveDialogBody')}
        actionLabel={t('membersAdmin.leaveGroup')}
        cancelLabel={t('membersAdmin.leaveDialogCancel')}
        destructive
        onAction={leaveFlow.confirmLeave}
      />
      {leaveFlow.error !== null ? (
        <Body size="sm" color="destructive">{leaveFlow.error}</Body>
      ) : null}

    </Stack>
  );
}
